/**
 * ESS Store — manages ESS project + comic listing state.
 *
 * Projects and comics are independent entities. The gallery renders them in
 * two sections (Comics on top, Projects below).
 */

import { create } from "zustand";
import type { ESSProjectMetadata } from "@amzn/a4k-creator-studio-shared";

export type ESSProject = ESSProjectMetadata & {
  /** Base64 JPEG — server-side enrichment for project-card cover rendering. */
  coverImageBase64?: string;
};

/** Comic row shape as emitted by the server — comic metadata + pre-fetched cover. */
export interface ESSComic {
  comicId: string;
  storyId?: string;
  title?: string;
  coverImageId?: string;
  pageCount: number;
  createdAt: number;
  updatedAt: number;
  /** Base64 JPEG (without data URI prefix) — pre-fetched by the server from S3. */
  coverImageBase64?: string;
}

export interface SharedComic {
  shareId: string;
  comicId: string;
  senderName?: string;
  senderAvatarUri?: string;
  title?: string;
  coverImageUrl?: string;
  pageCount: number;
  sharedAt: number;
}

interface ESSState {
  // Projects
  projects: ESSProject[];
  projectsNextToken?: string;
  setProjects: (projects: ESSProject[], nextToken?: string) => void;
  appendProjects: (projects: ESSProject[], nextToken?: string) => void;

  // Comics
  comics: ESSComic[];
  comicsNextToken?: string;
  setComics: (comics: ESSComic[], nextToken?: string) => void;
  appendComics: (comics: ESSComic[], nextToken?: string) => void;
  removeComic: (comicId: string) => void;
  removeProject: (storyId: string) => void;

  // Shared with me
  sharedComics: SharedComic[];
  sharedComicsNextToken?: string;
  setSharedComics: (comics: SharedComic[], nextToken?: string) => void;
  appendSharedComics: (comics: SharedComic[], nextToken?: string) => void;
}

export const useESSStore = create<ESSState>((set) => ({
  projects: [],
  projectsNextToken: undefined,
  comics: [],
  comicsNextToken: undefined,
  sharedComics: [],
  sharedComicsNextToken: undefined,

  setProjects: (projects, nextToken) => set({ projects, projectsNextToken: nextToken }),
  appendProjects: (projects, nextToken) => set((state) => ({
    projects: [...state.projects, ...projects],
    projectsNextToken: nextToken,
  })),

  setComics: (comics, nextToken) => set({ comics, comicsNextToken: nextToken }),
  appendComics: (comics, nextToken) => set((state) => ({
    comics: [...state.comics, ...comics],
    comicsNextToken: nextToken,
  })),

  removeComic: (comicId) => set((state) => ({
    comics: state.comics.filter((c) => c.comicId !== comicId),
  })),
  removeProject: (storyId) => set((state) => ({
    projects: state.projects.filter((p) => p.storyId !== storyId),
  })),

  setSharedComics: (comics, nextToken) => set({ sharedComics: comics, sharedComicsNextToken: nextToken }),
  appendSharedComics: (comics, nextToken) => set((state) => ({
    sharedComics: [...state.sharedComics, ...comics],
    sharedComicsNextToken: nextToken,
  })),
}));

if (__DEV__ && typeof window !== "undefined") {
  (window as any).useESSStore = useESSStore;
}
